import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { Container, Row, Col } from 'react-bootstrap';
import { OATextField } from '../../../components/fields';
import OAButton from '../../../components/widgets/OAButton';
import * as Yup from 'yup';
import { useContext, useState, useRef } from 'react';
import LedgerContext from '../context/LedgerContext';

interface IUploadSettlementCsvModal {
  onHide: () => void;
}

const init = {
  paymentMethod: '',
  file: null
};

const UploadCsvSchema = Yup.object().shape({
  file: Yup.mixed().required('Please select a CSV file')
});

export default function UploadSettlementCsvModal(props: IUploadSettlementCsvModal) {
  const { uploadSettlementCsv } = useContext(LedgerContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const downloadSampleCsv = () => {
    const csvContent = `entity_id,amount,currency,entity_created_at,transfers_notes,payments_notes,reversal_notes,settlement_id,settled_at,settlement_utr,arn,additional_utr`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample_settlement_transactions.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = (values: any, actions: FormikHelpers<any>) => {
    setLoading(true);

    const formData = new FormData();
    formData.append('file', values.file);
    // Add paymentMethod to FormData (not query param)
    if (values.paymentMethod) {
      formData.append('paymentMethod', values.paymentMethod);
    }
    // orgId will be added inside uploadSettlementCsv function

    // Remove paymentMethod from second parameter (no longer used as query param)
    uploadSettlementCsv(formData, undefined, (res: any) => {
      setLoading(false);
      if (res?.success !== false) {
        actions.resetForm();
        setSelectedFileName('');
        props?.onHide();
      }
    });
  };

  return (
    <Formik
      initialValues={init}
      onSubmit={handleSubmit}
      validationSchema={UploadCsvSchema}
      enableReinitialize>
      {(formikProps: FormikProps<any>) => {
        return (
          <Container fluid as={Form} className='p-0'>
            <Row className='mb-4'>
              <Col lg={12}>
                <OATextField
                  name='paymentMethod'
                  label='Payment Method'
                  placeholder='e.g., NEFT, RTGS, IMPS'
                />
              </Col>
            </Row>

            <Row className='mb-4'>
              <Col lg={12}>
                <label className='form-label fw-bold'>
                  CSV File <span className='text-danger'>*</span>
                </label>
                <div className='d-flex align-items-center'>
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='.csv'
                    style={{ display: 'none' }}
                    onChange={(event: any) => {
                      const file = event.target.files?.[0];
                      if (file) {
                        formikProps.setFieldValue('file', file);
                        setSelectedFileName(file.name);
                      }
                    }}
                  />
                  <OAButton
                    type='button'
                    className='btn-light-primary me-3'
                    label='Choose File'
                    onClick={() => fileInputRef.current?.click()}
                  />
                  <span className='text-muted'>
                    {selectedFileName || 'No file selected'}
                  </span>
                </div>
                {formikProps.errors.file && formikProps.touched.file && (
                  <div className='text-danger mt-2' style={{ fontSize: '0.875rem' }}>
                    {formikProps.errors.file as string}
                  </div>
                )}
                <div className='mt-6'>
                  <OAButton
                    type='button'
                    className='btn-sm btn-active-primary'
                    label='Download Sample CSV'
                    onClick={downloadSampleCsv}
                  />
                </div>
              </Col>
            </Row>

            <div className='d-flex justify-content-end mt-5'>
              <OAButton
                className='btn-light-primary btn-active-light-primary me-3'
                label='Cancel'
                onClick={() => {
                  formikProps?.resetForm();
                  setSelectedFileName('');
                  props?.onHide();
                }}
              />
              <OAButton
                type='submit'
                disabled={!formikProps?.values.file}
                isLoading={loading}
                loadingText='Uploading...'
                label='Upload CSV'
              />
            </div>
          </Container>
        );
      }}
    </Formik>
  );
}